import { Popover, PopoverBody, PopoverContent, PopoverTrigger, useDisclosure } from '@chakra-ui/react';
import { formatAddress } from '@pelicargo/utils';
import { useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { IconTruck } from '../Icons/IconTruck';
import { SimpleField } from '../SimpleField';
import { ClosestAirportPicker } from './ClosestAirportPicker';
import { SimplePickerField } from './SimplePickerField';

type Props = { addressName?: string; airportsName?: string; label?: string };

export const SimpleClosestAirportPicker = ({
  addressName = 'cargo_origin',
  airportsName = 'origin_airports',
  label = 'Pickup Address',
}: Props) => {
  const initialFocusRef = useRef<HTMLInputElement>(null);
  const { control } = useFormContext();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [originAirports, cargoOrigin] = useWatch({
    control,
    name: [airportsName, addressName],
  });

  const name = useMemo(() => formatAddress(cargoOrigin), [cargoOrigin]);

  const originValue = useMemo(() => {
    if (name) {
      return name;
    } else if (originAirports?.length) {
      return originAirports.map((airport) => airport.name).join(', ');
    }
    return label;
  }, [name, originAirports, label]);

  return (
    <Popover isOpen={isOpen} onClose={onClose} initialFocusRef={initialFocusRef} matchWidth>
      <PopoverTrigger>
        <SimpleField
          name={addressName}
          removePadding
          w="full"
          h="fit-content"
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
          }}
        >
          <SimplePickerField
            label={label}
            displayValue={originValue}
            isSelected={originValue !== label}
            icon={IconTruck}
            onOpen={onOpen}
          />
        </SimpleField>
      </PopoverTrigger>
      <PopoverContent w="full" minW="unset" maxH={520} overflow="auto" boxShadow="2xl">
        <PopoverBody
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
          }}
        >
          <ClosestAirportPicker addressField={addressName} onClose={onClose} initialFocusRef={initialFocusRef} />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
